import { Box, makeStyles, Paper, Tab, Tabs } from '@material-ui/core';
import { Filter1, Filter2, Filter3, Filter4, Filter5, FilterNone } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { generatePath } from 'react-router-dom';

import { useAuth } from '../../../../contexts/AuthContext/AuthContext';
import { hasBenchwiseAccess } from '../../../../core/utils/access.utils';
import { ROUTES } from '../../../../routes';
import { useBenchmarkDispatch, useBenchmarkState } from '../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../Context/BenchmarkContext.types';
import BenchwiseReportTab from './BenchwiseReport';
import CompaniesInfo from './CompaniesMatrix';
import CompanyPage from './CompanyResolve';
import RefinementWrapper from './Refinement';
import ResultsPanel from './ResultsPanel';
import SummaryInfo from './SummaryInfo/SummaryInfo';

export interface TabItem {
  name: string;
  label: React.ReactNode;
  component: React.ReactNode;
  show: boolean;
}

export const TabNamesEnum = {
  REFINEMENT: 'refinement',
  SUMMARY: 'summary',
  MANUAL: 'manual',
  MATRIX: 'matrix',
  RESULTS: 'results',
  BENCHWISE: 'benchwise',
};

const tabLabel = (label: string, icon: React.ReactNode) => {
  return (
    <Box style={{ display: 'flex' }}>
      {icon}
      <span style={{ marginLeft: '8px' }}>{label}</span>
    </Box>
  );
};

const benchwiseReportTabLabel = (
  label: string,
  icon: React.ReactNode,
  ai?: boolean,
  classes?: any,
) => (
  <Box style={{ display: 'flex', alignItems: 'center' }}>
    {icon}
    <span style={{ marginLeft: '8px' }}>
      CompID
      {ai && <span className={classes.aiGradient}> [AI] </span>}
      Report
    </span>
  </Box>
);

const BenchmarkTabs: React.FC = () => {
  const classes = useStyles();
  const { user } = useAuth();
  const benchmarkDispatch = useBenchmarkDispatch();
  const { activeTab, activeBenchmark } = useBenchmarkState();
  const [benchwiseEnabled, setBenchwiseEnabled] = useState<boolean>(false);

  useEffect(() => {
    setBenchwiseEnabled(hasBenchwiseAccess(user));
  }, [user]);

  // Listen for activeTab changes from SetClickedBenchmarkTabName action
  useEffect(() => {
    if (activeBenchmark && activeBenchmark.id && activeTab) {
      const newPath = generatePath(ROUTES.benchmarking.benchmarkTab, {
        benchmarkId: activeBenchmark.id,
        tabName: activeTab,
      });
      window.history.replaceState(null, '', newPath);
    }
  }, [activeTab, activeBenchmark]);

  const handleChange = (event: React.ChangeEvent<{}>, tab: string) => {
    ReactGA.event({ category: 'BENCHMARK', action: 'TAB_CLICK', label: tab });
    benchmarkDispatch({
      type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
      payload: tab,
    });
  };

  const tabItems: TabItem[] = [
    {
      name: TabNamesEnum.REFINEMENT,
      component: <RefinementWrapper />,
      label: tabLabel('Search Refinement', <FilterNone />),
      show: !!activeBenchmark?.refinementSearchId,
    },
    {
      name: TabNamesEnum.SUMMARY,
      component: <SummaryInfo />,
      label: tabLabel('Search Summary', <Filter1 />),
      show: true,
    },
    {
      name: TabNamesEnum.MANUAL,
      component: <CompanyPage />,
      label: tabLabel('Manual Acceptance/Rejection', <Filter2 />),
      show: true,
    },
    {
      name: TabNamesEnum.MATRIX,
      component: <CompaniesInfo />,
      label: tabLabel('Acceptance/Rejection summary matrix', <Filter3 />),
      show: true,
    },
    {
      name: TabNamesEnum.RESULTS,
      component: <ResultsPanel />,
      label: tabLabel('Benchmarking results', <Filter4 />),
      show: true,
    },
    {
      name: TabNamesEnum.BENCHWISE,
      component: <BenchwiseReportTab />,
      label: benchwiseReportTabLabel('CompID', <Filter5 />, true, classes),
      show: benchwiseEnabled,
    },
  ];

  return (
    <Box className={classes.tabsContainer}>
      <Paper square className={classes.tabItems}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          centered
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {tabItems.map(
            (tabItem: TabItem) =>
              tabItem.show && (
                <Tab
                  id={`full-width-tab-${tabItem.name}`}
                  key={tabItem.name}
                  value={tabItem.name}
                  label={tabItem.label}
                />
              ),
          )}
        </Tabs>
      </Paper>
      <Box className={classes.tabsBox}>
        {tabItems.map((tabItem: TabItem) => {
          return (
            activeTab === tabItem.name && (
              <Box role="tabpanel" id={`full-width-tabpanel-${tabItem.name}`} key={tabItem.name}>
                {tabItem.component}
              </Box>
            )
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  tabsBox: {
    overflowY: 'auto',
  },
  tabItems: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  tabsContainer: {
    position: 'relative',
    '& .Mui-selected': {
      '& .MuiTab-wrapper': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiTab-wrapper': {
      textTransform: 'capitalize',
      color: theme.palette.secondary.dark,
    },
    '& .MuiTab-root': {
      [theme.breakpoints.down('md')]: {
        minWidth: '100px',
      },
    },
  },
  aiGradient: {
    background: 'linear-gradient(90deg, #00b4d8 0%, #43ea7f 40%, #3a86ff 70%, #38b000 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontWeight: 500,
    fontSize: 'inherit',
    letterSpacing: '0.05em',
    display: 'inline-block',
    verticalAlign: 'middle',
    margin: '0px 4px 2px 4px;',
  },
}));

export default BenchmarkTabs;
